<template>
  <v-app v-resize="onAppResize">
    <v-navigation-drawer v-model="drawer" fixed app class="app-sidebar">
      <v-list dense>
        <li v-for="(item,index) in navItems" :key="index">
          <v-list-tile :to="item.to">
            <v-list-tile-action>
              <v-icon>{{item.icon}}</v-icon>
            </v-list-tile-action>
            <v-list-tile-content>
              <v-list-tile-title>{{item.title}}</v-list-tile-title>
            </v-list-tile-content>
          </v-list-tile>
        </li>
        <li>
          <v-list-tile href="https://status.strava.com/" target="_blank">
            <v-list-tile-action>
              <v-icon>cast</v-icon>
            </v-list-tile-action>
            <v-list-tile-content>
              <v-list-tile-title>Strava system check</v-list-tile-title>
            </v-list-tile-content>
          </v-list-tile>
        </li>
      </v-list>
    </v-navigation-drawer>
    <v-toolbar class="app-header" dark fixed app style="width: 100%">
      <!-- <v-toolbar class="app-header no-box-shadow" dark fixed app style="width: 100%"> -->
      <v-toolbar-side-icon @click.stop="drawer = !drawer"></v-toolbar-side-icon>
      <v-toolbar-title class="font-weight-thin ml-0 display-1 hidden-xs-only">{{ name }}</v-toolbar-title>
      <v-layout align-end justify-end>
        <session />
      </v-layout>
    </v-toolbar>
    <v-content>
      <v-snackbar v-model="showDebugMessage" top>{{ debugMessage }}</v-snackbar>
      <div class="context-container">
        <portal-target name="context-portal"></portal-target>
      </div>
      <v-container fluid fill-height class="content-container">
        <!-- <auth-container> -->
        <router-view />
        <!-- </auth-container> -->
      </v-container>
    </v-content>
    <v-footer class="app-footer" app ref="footer">
      <v-layout align-start>
        <span class="white--text">
          <h5 class="font-weight-thin ml-2 hidden-xs-only">&copy; Avondale Computer Systems 2019</h5>
          <h5
            v-if="!loadingMessage"
            class="font-weight-thin ml-2 hidden-sm-and-up"
          >&copy; AC Systems 2019</h5>
        </span>
      </v-layout>
      <v-layout align-end justify-end mr-2 class="white-text">
        <div v-if="!loadingMessage" class="d-flex">
          <img :height="32" src="./assets/api_logo_pwrdBy_strava_horiz_white.png" />
          <!-- <span class="white--text">{{sizeInfo}}</span> -->
        </div>

        <span v-if="loadingMessage" class="white--text d-inline-block">
          <table>
            <tr>
              <v-progress-linear
                class="d-flex m-0 mr-1"
                style="width: 100px"
                v-if="mode === 'determinate'"
                v-model="loadingPercentage"
              />
              <v-progress-circular
                v-if="mode === 'indeterminate'"
                indeterminate
                size="20"
                class="mr-1"
              ></v-progress-circular>
              <td>
                <h4 class="font-weight-thin d-inline-block">{{loadingMessage}}</h4>
              </td>
            </tr>
          </table>
        </span>
      </v-layout>
      <loading-dialog
        :is-loading="loadingMessage !== null"
        :message="loadingMessage"
        :percentage="loadingPercentage"
        :indeterminate="mode === 'indeterminate'"
      />
    </v-footer>
  </v-app>
</template>

<script>
import * as Sentry from '@sentry/browser';
import LoadingDialog from '@/ux/generic-components/LoadingDialog.vue';
import Session from '@/ux/components/Session.vue';
import AuthContainer from '@/ux/components/AuthContainer.vue';
import menuBuilder from '@/nav/menuBuilder';
import eventBus from '@/events/eventBus';
import SizeWatcher from '@/library/SizeWatcher';
import sizeAwareMixin from '@/mixins/sizeAwareMixin';
import encryptor from '@/library/Encryptor';
import logger from '@/domain/ClientLogger';

export default {
  components: { Session, AuthContainer, LoadingDialog },
  data: () => ({
    name: process.env.VUE_APP_NAME,
    drawer: null,
    navItems: [],
    loadingMessage: null,
    loadingPercentage: null,
    mode: 'indeterminate',
    sizeInfo: '',
    debugMessage: null,
    showDebugMessage: false,
  }),
  props: {
    source: String,
  },
  mixins: [sizeAwareMixin],
  mounted() {
    Sentry.captureMessage('Application started');
    eventBus.on('sign-in', this.onUserChange);
    eventBus.on('sign-out', this.onUserChange);
    eventBus.on('debug-message', this.onDebugMessage);
    eventBus.on('loading-start', this.onLoadingStart);
    eventBus.on('loading-update', this.onLoadingUpdate);
    eventBus.on('loading-end', this.onLoadingEnd);
    this.navItems = menuBuilder.getNavItems();
    this.sw = new SizeWatcher(() => this.$vuetify.breakpoint);
  },
  beforeDestroy() {
    eventBus.off('sign-in', this.onUserChange);
    eventBus.off('sign-out', this.onUserChange);
    eventBus.off('debug-message', this.onDebugMessage);
    eventBus.off('loading-start', this.onLoadingStart);
    eventBus.off('loading-update', this.onLoadingUpdate);
    eventBus.off('loading-end', this.onLoadingEnd);
  },
  methods: {
    onAppResize() {
      this.$store.commit('setAppViewport', { footerTop: this.$refs.footer.$el.offsetTop });
    },
    onResize(args) {
      this.sizeInfo = `${args.name} ${args.width}x${args.height}`;
    },
    onUserChange(m) {
      this.navItems = menuBuilder.getNavItems();
    },
    onDebugMessage(m) {
      this.showDebugMessage = true;
      this.debugMessage = m;
    },
    onLoadingStart(m) {
      this.mode = 'indeterminate';
      this.loadingMessage = m;
    },
    onLoadingUpdate(args) {
      this.mode = 'determinate';
      this.loadingMessage = args.message;
      this.loadingPercentage = args.percentage;
    },
    onLoadingEnd(m) {
      this.loadingMessage = null;
    },
  },
};
</script>

<style lang="scss" scoped>
h1 {
  background: $app-color;
}
</style>